'use client'
import LazyImage from '@/src/components/common/ui/LazyImage'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box, Button, Container, InputBase, Link, Typography } from '@mui/material'
import { useLocale } from 'next-intl'
import { useState } from 'react'

export interface PromoCard {
    id: string
    kind: 'link' | 'email'
    title: string
    description: string
    imageUrl: string
    href: string
    ctaLabel?: string
}

export interface HomePromoCardsProps {
    cards: PromoCard[]
    getNowLabel: string
    emailPlaceholder: string
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Email capture for the ladies-only card. No subscribe endpoint exists in akaashpath yet,
// so a valid email enables the CTA that routes to the full ladies-only offers page where
// signup completes; the typed value is not transmitted anywhere.
const EmailCapture: React.FC<{ href: string; getNowLabel: string; placeholder: string }> = ({
    href,
    getNowLabel,
    placeholder
}) => {
    const [email, setEmail] = useState('')
    const valid = EMAIL_RE.test(email.trim())

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                height: 48,
                border: 1,
                borderColor: 'borders.default',
                borderRadius: '10px',
                p: 0.5,
                pl: 1.5
            }}
        >
            <InputBase
                type="email"
                value={email}
                onChange={event => setEmail(event.target.value)}
                placeholder={placeholder}
                inputProps={{ 'aria-label': placeholder }}
                sx={{ flex: 1, fontSize: 14, fontWeight: 500, color: 'textColors.primary' }}
            />
            <Button
                href={href}
                target="_blank"
                rel="noopener"
                disabled={!valid}
                variant="customVariantPrimary"
                sx={{
                    px: 2.5,
                    height: '100%',
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: '10px',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                }}
            >
                {getNowLabel}
            </Button>
        </Box>
    )
}

// Pure presentation: 3 promo cards (image, title, description, and either a text CTA or an
// email-capture form). Mirrors me-flyin's adsBg promo strip in the akaashpath card style.
const HomePromoCards: React.FC<HomePromoCardsProps> = ({ cards, getNowLabel, emailPlaceholder }) => {
    const locale = useLocale()
    const isRTL = locale === 'ar'
    const Chevron = isRTL ? ChevronLeftIcon : ChevronRightIcon

    return (
        <Box sx={{ py: { xs: 3, md: 4 } }}>
            <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                        gap: 3,
                        alignItems: 'stretch'
                    }}
                >
                    {cards.map(card => (
                        <Box
                            key={card.id}
                            sx={{
                                bgcolor: 'neutral.white',
                                border: 1,
                                borderColor: 'borders.default',
                                borderRadius: 2,
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            {/* Image bleeds to the card edges; only the text block is padded. */}
                            <Box sx={{ position: 'relative', height: 200, width: '100%' }}>
                                <LazyImage src={card.imageUrl} alt={card.title} sizes="(max-width: 900px) 100vw, 33vw" />
                            </Box>
                            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'textColors.primary' }}>
                                    {card.title}
                                </Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 400, color: 'textColors.secondary', mt: 1, lineHeight: 1.5 }}>
                                    {card.description}
                                </Typography>

                                <Box sx={{ mt: 2.5 }}>
                                    {card.kind === 'email' ? (
                                        <EmailCapture href={card.href} getNowLabel={getNowLabel} placeholder={emailPlaceholder} />
                                    ) : (
                                        <Link
                                            href={card.href}
                                            target="_blank"
                                            rel="noopener"
                                            underline="none"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                color: 'primary.main',
                                                fontWeight: 600,
                                                fontSize: 14,
                                                '&:hover .promo-cta-arrow': { transform: isRTL ? 'translateX(-3px)' : 'translateX(3px)' }
                                            }}
                                        >
                                            {card.ctaLabel}
                                            <Chevron
                                                className="promo-cta-arrow"
                                                sx={{ fontSize: 20, transition: 'transform 0.2s' }}
                                            />
                                        </Link>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Container>
        </Box>
    )
}

export { HomePromoCards }
export default HomePromoCards
